import type { FC } from 'react';
import { StyleSheet, Text, useWindowDimensions, View, ScrollView } from 'react-native';
import { useHeaderHeight } from '@react-navigation/elements';
import { useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import type { StackNavigationProp } from '@react-navigation/stack';
import moment from 'moment';
import { PrimaryButton } from 'src/presentation/components/molecules/Button';
import BackgroundPattern from 'src/presentation/components/atoms/BackgroundPattern';
import { useMessagesStore } from 'src/domain/stores/useMessagesStore';
import { useLeaveAppStore } from 'src/domain/stores/useLeaveAppStore';
import Colors from 'src/core/utils/Colors';
import ThermenImage from 'src/presentation/components/molecules/ThermenImage';
import LoadingIndicator from 'src/presentation/components/molecules/LoadingIndicator';
import ErrorView from 'src/presentation/components/organisms/ErrorView';
import { HtmlView } from 'src/presentation/components/organisms/HtmlView';
import { useTabBarInset } from 'src/presentation/hooks/useTabBarInset';
import useAppStateScreenListener from 'src/presentation/hooks/useAppStateScreenListener';
import type { MessageDetailScreenParams, MessagesNavigation } from 'src/presentation/navigation/types/NavigationTypes';
import Dimens from 'src/core/utils/Dimens';
import Fonts from 'src/core/utils/Fonts';
import Constants from 'src/core/utils/Constants';

const MessageDetailScreen: FC = () => {
	const route = useRoute<RouteProp<{ params: MessageDetailScreenParams }>>();
	const navigation = useNavigation<StackNavigationProp<MessagesNavigation>>();
	const { height } = useWindowDimensions();
	const headerHeight = useHeaderHeight();
	const tabBarInset = useTabBarInset();

	const {
		messageDetailLoading,
		messageDetailErrorMessage,
		initMessageDetail,
		getApiMessage,
		putApiMessageRead,
		findCategory,
	} = useMessagesStore();
	const { confirmLeaving } = useLeaveAppStore();
	const { message } = route.params;

	const fetchData = async () => {
		if (!message) {
			return;
		}

		await getApiMessage({ messageId: message.id.toString() });
	};

	useAppStateScreenListener(
		{
			onInit: () => {
				if (!message) {
					navigation.goBack();
					return;
				}

				initMessageDetail();

				if (!message.read) {
					putApiMessageRead({ messageId: message.id.toString() });
				}
			},
			onFocussed: fetchData,
			onForegrounded: fetchData,
		},
		[message?.id],
	);

	const categoryId = message?.category;
	const category = categoryId === null || categoryId === undefined ? undefined : findCategory(categoryId);

	return (
		<View style={[styles.modalView, { paddingTop: headerHeight, maxHeight: height }]}>
			<BackgroundPattern />

			{messageDetailLoading ? (
				<LoadingIndicator style={{ flex: Dimens.flex1 }} />
			) : messageDetailErrorMessage ? (
				<ErrorView onPress={fetchData} />
			) : (
				<ScrollView
					contentContainerStyle={{
						paddingHorizontal: Dimens.padding18,
						paddingBottom: tabBarInset + Dimens.padding24,
					}}
				>
					<Text style={styles.info}>
						{moment(message?.published_at, Constants.dateFormatServer).format(Constants.dateFormatDisplayShortPadded)}
						{' - '}
						{category?.name}
					</Text>
					<Text style={styles.title}>{message?.title}</Text>
					<ThermenImage
						contentFit={'cover'}
						source={message?.media ? { uri: message.media } : null}
						style={[styles.image]}
					/>

					{message?.content && <HtmlView html={message?.content} />}

					{!!message?.button_link && (
						<View style={{ marginTop: Dimens.padding20 }}>
							<PrimaryButton
								extraStyle={{ alignSelf: 'flex-start' }}
								label={message?.button_text ?? undefined}
								onPress={() => confirmLeaving({ url: message?.button_link ?? '' })}
							/>
						</View>
					)}
				</ScrollView>
			)}
		</View>
	);
};

export default MessageDetailScreen;

const styles = StyleSheet.create({
	modalView: {
		flex: Dimens.flex1,
	},
	info: {
		marginBottom: Dimens.padding10,
		color: Colors.gray650,
		fontFamily: Fonts.barlowMedium,
		fontSize: Dimens.fontSize14,
		lineHeight: Dimens.lineHeight21,
	},
	title: {
		fontFamily: Fonts.ptSerif,
		fontSize: Dimens.fontSize24,
		lineHeight: Dimens.lineHeight32,
		marginBottom: Dimens.padding32,
	},
	image: {
		width: '100%',
		height: Dimens.size213,
		borderRadius: Dimens.borderRadius4,
		marginBottom: Dimens.padding32,
		backgroundColor: Colors.gray200,
	},
});
